/**
 * Live console rendering classified RTCM/NMEA frames coming from a mountpoint.
 *
 * Subscribes via `useMountpointStream` and renders the rolling buffer in a
 * monospace `<pre>`. Auto-scrolls to the bottom only when the user is already
 * pinned there — scrolling up implicitly pauses follow so the operator can
 * inspect a row without it jumping away.
 */

import { useEffect, useMemo, useRef } from "react";

import {
  type StreamDirection,
  type StreamRole,
  useMountpointStream,
} from "@/hooks/useMountpointStream";
import { cn } from "@/lib/utils";

interface StreamConsoleProps {
  mountpointId: string;
  role: StreamRole;
  sessionId?: string;
  direction?: StreamDirection;
  enabled: boolean;
}

const KIND_CLASSES: Record<string, string> = {
  RTCM3: "text-emerald-400",
  NMEA: "text-sky-400",
  RAW: "text-zinc-500",
};

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function pad3(n: number): string {
  if (n < 10) return `00${n}`;
  if (n < 100) return `0${n}`;
  return String(n);
}

function formatTs(ts: number): string {
  const d = new Date(ts * 1000);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}.${pad3(d.getMilliseconds())}`;
}

export function StreamConsole({
  mountpointId,
  role,
  sessionId,
  direction,
  enabled,
}: StreamConsoleProps) {
  const { frames, status, paused, setPaused, clear } = useMountpointStream({
    enabled,
    mountpointId,
    role,
    sessionId,
    direction,
  });

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stickyBottomRef = useRef(true);

  // Auto-scroll to bottom on new frames, but only if we were already pinned.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (!stickyBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [frames]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const slack = 24; // pixels from bottom that still count as "at bottom"
    stickyBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < slack;
  };

  const statusLabel = useMemo(() => {
    switch (status) {
      case "open":
        return "live";
      case "connecting":
        return "conectando…";
      case "closed":
        return "desconectado";
      case "error":
        return "error";
      default:
        return "inactivo";
    }
  }, [status]);

  return (
    <div className="flex flex-col gap-2 h-full">
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "inline-block h-2 w-2 rounded-full",
              status === "open" ? "bg-emerald-500" : "bg-zinc-400",
            )}
          />
          <span>{statusLabel}</span>
          <span className="text-zinc-400">·</span>
          <span>{frames.length} frames</span>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="px-2 py-1 rounded border border-zinc-300 hover:bg-zinc-100 text-xs"
            onClick={() => setPaused(!paused)}
          >
            {paused ? "Reanudar" : "Pausar"}
          </button>
          <button
            type="button"
            className="px-2 py-1 rounded border border-zinc-300 hover:bg-zinc-100 text-xs"
            onClick={clear}
          >
            Limpiar
          </button>
        </div>
      </div>
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex-1 overflow-auto rounded border border-zinc-800 bg-zinc-950 p-3 font-mono text-xs leading-snug"
        style={{ minHeight: 320 }}
      >
        {frames.length === 0 ? (
          <div className="text-zinc-500">
            {status === "open"
              ? "Esperando datos del stream…"
              : "Sin datos. La consola muestra los frames que cruzan en vivo."}
          </div>
        ) : (
          frames.map((f, idx) => (
            <div key={`${f.ts}-${idx}`} className="whitespace-pre">
              <span className="text-zinc-500">{formatTs(f.ts)}</span>{"  "}
              <span className={cn("font-semibold", KIND_CLASSES[f.kind] ?? "")}>
                {f.kind.padEnd(5, " ")}
              </span>{"  "}
              <span className="text-zinc-300">
                {f.subtype ? `type=${f.subtype.padEnd(8, " ")}` : "         ".padEnd(13, " ")}
              </span>
              <span className="text-zinc-400">len={String(f.length).padEnd(5, " ")}</span>
              <span className="text-zinc-500">{f.hex}</span>
              <span className="text-zinc-600">{"  "}{f.ascii}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
